import get from 'lodash.get';
import React, { Suspense, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useArchitectLinks } from '../../context/ArchitectLinksContext';
import { getColorClasses } from '../../helpers/colorHelper';
import { slugify } from '../../utils/slugify';
import LoadingScreen from '../loading/LoadingScreen';
import CourseImage from './CourseImage';

function ListCards({
  paginatedData = [], // Default to an empty array if not provided
  toggleDetailsVisibility,
  detailsVisibility,
  isImageAvailable,
  columns = [], // Default to an empty array if not provided
  courseImages,
  data,
  showMap,
  toggleView,
  getPaginatedData,
  postcodeCoords,
  changePage,
  handleChangeCardsPerPage,
  cardsPerPage,
  totalPages,
  currentPage,
}) {
  const { getArchitectSlug } = useArchitectLinks();
  const LazyCourseMap = React.lazy(() => import('../maps/CourseMap'));
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [showCheckboxes, setShowCheckboxes] = useState(false);

  // Exclude specific columns and initialize selected columns with up to first three
  useEffect(() => {
    const filteredColumns = columns.filter(
      (column) =>
        ![
          'Upvotes',
          'County Rank',
          'Course',
          'Country',
          'Country Rank',
          'County',
          'Style',
          'Club Name',
          'Course - Country Rank',
        ].includes(column.Header),
    );
    setAvailableColumns(filteredColumns);
    setSelectedColumns(filteredColumns.slice(0, 3).map((col) => col.accessor));
  }, [columns]);

  const handleColumnToggle = (accessor) => {
    setSelectedColumns((prev) => {
      const isSelected = prev.includes(accessor);
      if (isSelected) {
        return prev.filter((col) => col !== accessor);
      } else {
        // Allow adding more columns up to a total of 6
        if (prev.length < 9) {
          return [...prev, accessor];
        }
      }
      return prev; // Return the previous state if no changes are made
    });
  };

  if (!paginatedData || !columns) {
    return <div>Loading...</div>;
  }

  //console.log("Details Vis", detailsVisibility)
  //console.log("columns", columns)
  return (
    <>
      {/* Checkbox UI for toggling columns */}
      <div className="mx-auto w-11/12 py-2 px-6 rounded-xl border bg-white shadow-md mt-2">
        <h4 className="mx-auto text-xs font-bold py-1 flex justify-between items-center ">
          Data Selector:
          <button
            onClick={() => setShowCheckboxes(!showCheckboxes)}
            className="text-xs font-semibold p-1 rounded-md focus:outline-none bg-gray-900 hover:bg-gray-500 text-white w-[100px]"
            aria-expanded={showCheckboxes}
            aria-controls="checkbox-container"
          >
            {showCheckboxes ? 'Hide' : 'Show'}
          </button>
        </h4>
        {showCheckboxes && (
          <div
            id="checkbox-container"
            className="text-xs mx-auto gap-y-3 gap-x-1 grid grid-cols-2 md:grid-cols-7 transition-all duration-500 ease-in-out"
          >
            {availableColumns.map((column) => (
              <div
                className="flex flex-row flex-nowrap items-center"
                key={column.accessor}
              >
                <input
                  type="checkbox"
                  id={column.accessor}
                  name={column.accessor}
                  checked={selectedColumns.includes(column.accessor)}
                  onChange={() => handleColumnToggle(column.accessor)}
                  className="mr-1"
                />
                <label className="truncate" htmlFor={column.accessor}>
                  {column.Header}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Show TOP Dropdown */}
      <div className="mx-auto w-11/12 md:w-full my-2 grid grid-cols-3 gap-x-1 sm:flex sm:flex-row sm:justify-between sm:items-center sm:my-2 sm:px-6 sm:space-y-2 sm:space-x-4 ">
        {/* Show Dropdown */}
        <div
          className={`col-span-1 flex flex-row items-center space-x-2 ${
            showMap ? 'hidden' : ''
          }`}
        >
          <label
            htmlFor="cardsPerPage"
            className="xs:text-xs text-xs lg:text-base md:ml-12"
          >
            Show:
          </label>
          <select
            className="border min-w-[50px] w-16 rounded text-left text-sm p-1"
            value={cardsPerPage}
            onChange={handleChangeCardsPerPage}
          >
            <option value="1">1</option>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        {/* Pagination controls */}
        <div
          className={`col-span-2 flex flex-row justify-between items-center space-x-4 w-full sm:w-auto md:pr-12 ${
            showMap ? 'hidden' : ''
          }`}
        >
          <button
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage === 0}
            className="text-xs hover:cursor-pointer bg-gray-200 rounded-md p-1 border border-gray-400 w-fit"
          >
            Previous
          </button>
          <span className="xs:text-xs text-xs lg:text-base">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className="text-xs hover:cursor-pointer bg-gray-200 rounded-md p-1 border border-gray-400 w-fit"
          >
            Next
          </button>
        </div>
      </div>
      {showMap ? (
        <div className="mx-auto w-11/12">
          {/** This is aligning the same as DataTable on Web */}
          <div className="mx-auto bg-white rounded-lg border border-gray-300 p-2 hover:border-gray-400 my-2 h-[430px]">
            <Suspense fallback={<LoadingScreen type="spin" color="#000" />}>
              <LazyCourseMap
                visibleData={paginatedData}
                postcodeCoords={postcodeCoords}
              />
            </Suspense>
          </div>
        </div>
      ) : (
        <div className="mx-auto md:w-11/12 md:grid md:grid-cols-1 md:gap-4 mt-2">
          {paginatedData.map((dataset, index) => (
            <div
              key={
                dataset.temp_unique_id
                  ? `${dataset.temp_unique_id}_container`
                  : dataset.club_uid
                    ? `${dataset.club_uid}_container`
                    : dataset.course_uid
                      ? `${dataset.course_uid}_container`
                      : `missing_uid_${index}`
              }
              className="bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                <div className="col-span-1 min-h-[180px] md:min-h-0 overflow-hidden shrink-0">
                  <CourseImage
                    //key={`${dataset.course_uid}_image`}
                    courseName={
                      dataset.clubCourses?.[0]?.course_name ||
                      dataset?.course_name
                    }
                    clubName={
                      dataset.open_name === null ||
                      dataset.open_name === undefined
                        ? dataset.club_name || null
                        : null
                    }
                    openName={dataset.open_name || null}
                    courseImages={courseImages} //the array of image data collected in this component
                    visibleData={data}
                  />
                </div>
                <div className="col-span-1 w-full md:col-span-2 p-3 md:p-5 flex flex-col justify-center">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 md:gap-x-6 md:gap-y-2">
                  {(() => {
                    const cols = availableColumns.filter(
                      (c) =>
                        selectedColumns.includes(c.accessor) &&
                        c.accessor !== 'id' &&
                        ![
                          'course_name',
                          'country_rank',
                          'course_country',
                          'course_county',
                          'county_rank',
                          'course_style',
                          'club_name',
                        ].includes(c.accessor),
                    );
                    const collectionsCol = cols.find((c) => c.accessor === 'collections');
                    const architectCol = cols.find((c) => c.accessor === 'combined_designer_original');
                    const contributorCol = cols.find((c) => c.accessor === 'designer_contributor');
                    const otherCols = cols.filter(
                      (c) =>
                        !['collections', 'combined_designer_original', 'designer_contributor'].includes(
                          c.accessor,
                        ),
                    );

                    const renderTags = (column, rawValue) => {
                      const arr = Array.isArray(rawValue) ? rawValue : rawValue != null ? [rawValue] : [];
                      return arr.map((data) => {
                        const colorClasses = getColorClasses(column.accessor);
                        const {
                          backgroundColor,
                          textColor,
                          ringColor = 'ring-slate-400/30',
                        } = colorClasses;
                        const tagClasses = `inline-flex items-center rounded-full px-2 py-0.5 md:px-3 md:py-1 text-xs font-medium ring-1 ${ringColor} transition-all ${backgroundColor} ${textColor}`;
                        if (column.accessor === 'collections') {
                          return (
                            <Link
                              to={`/lists/golf-courses/collections/${slugify(data)}`}
                              key={`${data}-${dataset.course_uid}`}
                              className={`${tagClasses} hover:ring-2`}
                            >
                              {data}
                            </Link>
                          );
                        }
                        if (column.accessor === 'reciprocals') {
                          return (
                            <Link
                              key={`${data}-${dataset.course_uid}`}
                              to={`/lists/golf-clubs/${slugify(data)}`}
                              className={`${tagClasses} hover:ring-2`}
                            >
                              {data}
                            </Link>
                          );
                        }
                        if (
                          (column.accessor === 'combined_designer_original' ||
                            column.accessor === 'designer_contributor') &&
                          getArchitectSlug(data)
                        ) {
                          return (
                            <Link
                              key={`${data}-${dataset.course_uid}`}
                              to={`/lists/architects/${getArchitectSlug(data)}`}
                              className={`${tagClasses} hover:ring-2`}
                            >
                              {data}
                            </Link>
                          );
                        }
                        return (
                          <span
                            key={`${data}-${dataset.course_uid}`}
                            className={tagClasses}
                          >
                            {data}
                          </span>
                        );
                      });
                    };

                    return (
                      <>
                        {/* Row 1: Collections only – single row, no wrap */}
                        {collectionsCol && selectedColumns.includes(collectionsCol.accessor) && (() => {
                          const rawValue = get(dataset, collectionsCol.accessor);
                          const isEmpty = rawValue == null || rawValue === '' || (Array.isArray(rawValue) && rawValue.length === 0);
                          if (isEmpty) return null;
                          return (
                            <div
                              key={`collections-${dataset.course_uid}`}
                              className="py-1.5 md:py-2.5 border-b border-slate-100 min-w-0 col-span-full"
                            >
                              <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                                {collectionsCol.Header}
                              </dt>
                              <dd className="text-sm text-slate-800 font-medium flex flex-nowrap gap-1 md:gap-1.5 overflow-x-auto pb-1">
                                {renderTags(collectionsCol, rawValue)}
                              </dd>
                            </div>
                          );
                        })()}
                        {/* Row 2: Original Designer + Design Contributors in two columns */}
                        {(architectCol || contributorCol) &&
                          (selectedColumns.includes(architectCol?.accessor) ||
                            selectedColumns.includes(contributorCol?.accessor)) && (
                          <div
                            key={`designers-${dataset.course_uid}`}
                            className="grid grid-cols-2 gap-x-4 md:gap-x-6 py-1.5 md:py-2.5 border-b border-slate-100 min-w-0 col-span-full"
                          >
                            {architectCol && selectedColumns.includes(architectCol.accessor) && (
                              <div className="min-w-0">
                                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                                  {architectCol.Header}
                                </dt>
                                <dd className="text-sm text-slate-800 font-medium flex flex-wrap gap-1 md:gap-1.5">
                                  {renderTags(architectCol, get(dataset, architectCol.accessor))}
                                </dd>
                              </div>
                            )}
                            {contributorCol && selectedColumns.includes(contributorCol.accessor) && (
                              <div className="min-w-0">
                                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                                  {contributorCol.Header}
                                </dt>
                                <dd className="text-sm text-slate-800 font-medium flex flex-wrap gap-1 md:gap-1.5">
                                  {renderTags(contributorCol, get(dataset, contributorCol.accessor))}
                                </dd>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Other columns */}
                        {otherCols.map((column) => {
                          if (!selectedColumns.includes(column.accessor)) return null;
                          const rawValue = get(dataset, column.accessor);
                          const isEmpty =
                            rawValue == null ||
                            rawValue === '' ||
                            (Array.isArray(rawValue) && rawValue.length === 0);
                          if (!column.Cell && isEmpty) return null;
                          return (
                            <div
                              key={`${column.accessor}-${dataset.course_uid}`}
                              className="py-1.5 md:py-2.5 border-b border-slate-100 last:border-b-0 min-w-0 col-span-full"
                            >
                              {column.Header && (
                                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                                  {column.Header}
                                </dt>
                              )}
                              <dd className="text-sm text-slate-800 font-medium flex flex-wrap gap-1 md:gap-1.5">
                                {Array.isArray(rawValue)
                                  ? renderTags(column, rawValue)
                                  : column.Cell
                                    ? column.Cell({
                                        cell: { value: rawValue },
                                        row: { original: dataset },
                                      })
                                    : rawValue}
                              </dd>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                  </dl>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Show Dropdown */}
      <div className="mx-auto w-11/12 md:w-full my-1 grid grid-cols-3 gap-x-1 sm:flex sm:flex-row sm:justify-between sm:items-center sm:my-2 sm:px-6 sm:space-y-2 sm:space-x-4 ">
        {/* Show Dropdown */}
        <div
          className={`col-span-1 flex flex-row items-center space-x-2 ${
            showMap ? 'hidden' : ''
          }`}
        >
          <label
            htmlFor="cardsPerPage"
            className="xs:text-xs text-xs lg:text-base md:ml-12"
          >
            Show:
          </label>
          <select
            className="border min-w-[50px] w-16 rounded text-left text-sm p-1"
            value={cardsPerPage}
            onChange={handleChangeCardsPerPage}
          >
            <option value="1">1</option>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        {/* Pagination controls */}
        <div
          className={`col-span-2 flex flex-row justify-between items-center space-x-4 w-full sm:w-auto md:pr-12 ${
            showMap ? 'hidden' : ''
          }`}
        >
          <button
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage === 0}
            className="text-xs hover:cursor-pointer bg-gray-200 rounded-md p-1 border border-gray-400 w-fit"
          >
            Previous
          </button>
          <span className="xs:text-xs text-xs lg:text-base">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className="text-xs hover:cursor-pointer bg-gray-200 rounded-md p-1 border border-gray-400 w-fit"
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}

export default ListCards;
